import {
  BoundCommonBoundTypeEnum as BoundTypeEnum,
  CheckInFlight,
  JourneyElementCheckInStatusEnum as CheckInStatusEnum,
  Journey,
  JourneyElementCommon,
  LocationDictionaryItem,
  Traveler,
} from '@dapi/sdk';
import {
  AcceptanceRepository,
  CheckInFlightIdObjectRepository,
  combineCheckInFlightIdObjects,
  combineJourneyElementCommons,
  combineJourneys,
  combineJourneyTravelers,
  createAcceptance,
  createBoardingPassEligibility,
  createBoundCommon,
  createCheckInFlight,
  createCheckInFlightIdObject,
  createEligibility,
  createJourney,
  createJourneyElement,
  createJourneyElementCommon,
  createTraveler,
  JourneyTravelerRepository,
  LocationDictionaryItemRepository,
  MockJourney,
  RecursivePartial,
  todayPlusNdays,
} from '@dapi/sdk/spec';
import {
  contactEmail,
  filledAdTChdInfJourneyTravelers,
  multipleNotificationContacts,
  namedTraveler,
  twoAdtJourneyTravelers,
} from '../dictionaries';

/** An interface for the parameters required for the creation of a mock check in flight */
export interface CheckinFlightParameters {
  id?: number;
  originLocationCode?: string;
  destinationLocationCode?: string;
  departureTime?: string;
  arrivalTime?: string;
  daysOffset?: number;
  boundType?: BoundTypeEnum;
  departureLocation?: Partial<LocationDictionaryItem>;
  arrivalLocation?: Partial<LocationDictionaryItem>;
}

function constructCheckinFlight({id = 0,
  daysOffset = 2,
  arrivalTime = '10:00',
  boundType = 'OUTBOUND',
  departureTime = '09:00',
  originLocationCode = 'NCE',
  destinationLocationCode = 'LHR',
  arrivalLocation = LocationDictionaryItemRepository.LHR,
  departureLocation = LocationDictionaryItemRepository.NCE}: CheckinFlightParameters = {}): CheckInFlight {
  return createCheckInFlight({
    id,
    template: {
      boundId: `BOUND-${originLocationCode}-${destinationLocationCode}`,
      departure: {
        locationCode: originLocationCode,
        dateTime: todayPlusNdays(departureTime, daysOffset),
        location: departureLocation
      },
      arrival: {
        locationCode: destinationLocationCode,
        dateTime: todayPlusNdays(arrivalTime, daysOffset),
        location: arrivalLocation
      },
      bound: {
        id: `BOUND-${originLocationCode}-${destinationLocationCode}`,
        boundSummary: createBoundCommon({
          template: {
            boundType,
            originLocationCode,
            destinationLocationCode
          }
        })
      }
    }
  });
}

function constructJourneyElementCommon({id = 0,
  traveler,
  daysOffset = 2,
  journeyElementId = 0,
  arrivalTime = '10:00',
  boundType = 'OUTBOUND',
  departureTime = '09:00',
  originLocationCode = 'NCE',
  destinationLocationCode = 'LHR',
  arrivalLocation = LocationDictionaryItemRepository.LHR,
  departureLocation = LocationDictionaryItemRepository.NCE}: CheckinFlightParameters & {journeyElementId?: number;
    traveler?: Traveler;} = {}): JourneyElementCommon {
  return createJourneyElementCommon({
    id: journeyElementId,
    template: {
      journeyElement: createJourneyElement({
        id: journeyElementId,
        template: {
          traveler,
          flight: constructCheckinFlight({
            id,
            boundType,
            daysOffset,
            arrivalTime,
            departureTime,
            originLocationCode,
            destinationLocationCode,
            arrivalLocation,
            departureLocation
          })
        }
      })
    }
  });
}

export const journeyTemplate: RecursivePartial<Journey> = {
  contacts: [contactEmail],
  travelers: [namedTraveler]
};

export const multiPaxOutboundJourneyTemplate: RecursivePartial<Journey> = {
  contacts: [contactEmail],
  travelers: filledAdTChdInfJourneyTravelers,
  flights: [
    createCheckInFlightIdObject({
      id: 0,
      template: {
        checkInFlight: constructCheckinFlight()
      }
    })
  ],
  journeyElements: [
    constructJourneyElementCommon({
      journeyElementId: 0,
      traveler: filledAdTChdInfJourneyTravelers[0]
    }),
    constructJourneyElementCommon({
      journeyElementId: 1,
      traveler: filledAdTChdInfJourneyTravelers[1]
    }),
    constructJourneyElementCommon({
      journeyElementId: 2,
      traveler: filledAdTChdInfJourneyTravelers[2]
    })
  ]
};

export const multiPaxInboundJourneyTemplate: RecursivePartial<Journey> = {
  contacts: [contactEmail],
  travelers: filledAdTChdInfJourneyTravelers,
  flights: [
    createCheckInFlightIdObject({
      id: 1,
      template: {
        checkInFlight: constructCheckinFlight({
          id: 1,
          originLocationCode: 'LHR',
          destinationLocationCode: 'NCE',
          departureTime: '17:00',
          arrivalTime: '18:00',
          boundType: 'INBOUND',
          departureLocation: LocationDictionaryItemRepository.LHR,
          arrivalLocation: LocationDictionaryItemRepository.NCE
        })
      }
    })
  ],
  journeyElements: [
    constructJourneyElementCommon({
      id: 1,
      journeyElementId: 3,
      traveler: filledAdTChdInfJourneyTravelers[0],
      originLocationCode: 'LHR',
      destinationLocationCode: 'NCE',
      departureTime: '17:00',
      arrivalTime: '18:00',
      boundType: 'INBOUND',
      departureLocation: LocationDictionaryItemRepository.LHR,
      arrivalLocation: LocationDictionaryItemRepository.NCE
    }),
    constructJourneyElementCommon({
      id: 1,
      journeyElementId: 4,
      traveler: filledAdTChdInfJourneyTravelers[1],
      originLocationCode: 'LHR',
      destinationLocationCode: 'NCE',
      departureTime: '17:00',
      arrivalTime: '18:00',
      boundType: 'INBOUND',
      departureLocation: LocationDictionaryItemRepository.LHR,
      arrivalLocation: LocationDictionaryItemRepository.NCE
    }),
    constructJourneyElementCommon({
      id: 1,
      journeyElementId: 5,
      traveler: filledAdTChdInfJourneyTravelers[2],
      originLocationCode: 'LHR',
      destinationLocationCode: 'NCE',
      departureTime: '17:00',
      arrivalTime: '18:00',
      boundType: 'INBOUND',
      departureLocation: LocationDictionaryItemRepository.LHR,
      arrivalLocation: LocationDictionaryItemRepository.NCE
    })
  ]
};

export const multiPaxRoundTripJourneyTemplate: RecursivePartial<Journey> = {
  contacts: [contactEmail],
  travelers: filledAdTChdInfJourneyTravelers,
  flights: [
    multiPaxOutboundJourneyTemplate.flights![0],
    multiPaxInboundJourneyTemplate.flights![0]
  ],
  journeyElements: [
    multiPaxOutboundJourneyTemplate.journeyElements![0],
    multiPaxOutboundJourneyTemplate.journeyElements![1],
    multiPaxOutboundJourneyTemplate.journeyElements![2],
    multiPaxInboundJourneyTemplate.journeyElements![0],
    multiPaxInboundJourneyTemplate.journeyElements![1],
    multiPaxInboundJourneyTemplate.journeyElements![2]
  ]
};

export const multiLegMultiPaxJourneyTemplate: RecursivePartial<Journey> = {
  contacts: [contactEmail],
  travelers: twoAdtJourneyTravelers,
  flights: [
    createCheckInFlightIdObject({
      template: {
        checkInFlight: createCheckInFlight({
          template: {
            stops: [{
              arrivalDateTime: todayPlusNdays('09:20', 2),
              departureDateTime: todayPlusNdays('09:30', 2),
              duration: 600,
              locationCode: 'CDG'
            }]
          }
        })
      }
    })
  ],
  journeyElements: [
    createJourneyElementCommon({
      template: {
        journeyElement: createJourneyElement({
          template: {
            traveler: twoAdtJourneyTravelers[0],
            travelerId: twoAdtJourneyTravelers[0].id,
            legs: [
              {
                boardPointIataCode: 'NCE',
                offPointIataCode: 'CDG',
                scheduledArrivalDateTime: todayPlusNdays('9:20', 2),
                scheduledDepartureDateTime: todayPlusNdays('9:00', 2)
              },
              {
                boardPointIataCode: 'CDG',
                offPointIataCode: 'LHR',
                scheduledArrivalDateTime: todayPlusNdays('10:00', 2),
                scheduledDepartureDateTime: todayPlusNdays('9:30', 2)
              }
            ]
          }
        })
      }
    }),
    createJourneyElementCommon({
      template: {
        journeyElement: createJourneyElement({
          template: {
            traveler: twoAdtJourneyTravelers[1],
            travelerId: twoAdtJourneyTravelers[1].id,
            legs: [
              {
                boardPointIataCode: 'NCE',
                offPointIataCode: 'CDG',
                scheduledArrivalDateTime: todayPlusNdays('9:20', 2),
                scheduledDepartureDateTime: todayPlusNdays('9:00', 2)
              },
              {
                boardPointIataCode: 'CDG',
                offPointIataCode: 'LHR',
                scheduledArrivalDateTime: todayPlusNdays('10:00', 2),
                scheduledDepartureDateTime: todayPlusNdays('9:30', 2)
              }
            ]
          }
        })
      }
    })
  ]
};

export const multiLegJourneyTemplate: RecursivePartial<Journey> = {
  contacts: [contactEmail],
  travelers: [namedTraveler],
  flights: [
    createCheckInFlightIdObject({
      template: {
        checkInFlight: createCheckInFlight({
          template: {
            stops: [{
              arrivalDateTime: todayPlusNdays('09:20', 2),
              departureDateTime: todayPlusNdays('09:30', 2),
              duration: 600,
              locationCode: 'CDG'
            }]
          }
        })
      }
    })
  ],
  journeyElements: [
    createJourneyElementCommon({
      template: {
        journeyElement: createJourneyElement({
          template: {
            legs: [
              {
                boardPointIataCode: 'NCE',
                offPointIataCode: 'CDG',
                scheduledArrivalDateTime: todayPlusNdays('9:20', 2),
                scheduledDepartureDateTime: todayPlusNdays('9:00', 2)
              },
              {
                boardPointIataCode: 'CDG',
                offPointIataCode: 'LHR',
                scheduledArrivalDateTime: todayPlusNdays('10:00', 2),
                scheduledDepartureDateTime: todayPlusNdays('9:30', 2)
              }
            ]
          }
        })
      }
    })
  ]
};

export interface JourneyMockData {
  /** List of journeys */
  journeys: Journey[];

  /** List of journey element ids */
  journeyElementIds: string[];

  /** Accepted journey */
  acceptedJourney: Journey;

  /** List of traveler ids */
  travelerIds: string[];
}

/**
 * Create a list of journeys using the mock factory
 *
 * @param isMultiLeg boolean indicating if the journey is multi-leg
 * @param isMultiPax boolean indicating if the journey consists of multiple passengers
 */
export function getJourneyMockData(isMultiLeg = false, isMultiPax = false): JourneyMockData {
  const template = isMultiLeg ? (isMultiPax ? multiLegMultiPaxJourneyTemplate : multiLegJourneyTemplate) : journeyTemplate;
  const mockJourneys = combineJourneys([template]).items;
  return {
    journeys: mockJourneys,
    journeyElementIds: mockJourneys[0].journeyElements.map((je: JourneyElementCommon) => je.id),
    acceptedJourney: {
      ...mockJourneys[0],
      acceptance: AcceptanceRepository.ACCEPTED
    },
    travelerIds: mockJourneys[0].travelers.map((t) => t.id).filter((id): id is string => !!id)
  };
}

/**
 * Checks if the given journey is a multi-leg journey
 *
 * @param journey the given journey
 * @returns boolean indicating if the journey is a multi-leg journey
 */
export function isMultiLegJourney(journey: Journey): boolean {
  return (journey.journeyElements?.[0]?.journeyElement.legs?.length || 0) > 1;
}

/**
 * Normalizes the journey elements of the given journey with the template provided
 *
 * @param journey the given journey
 * @param template the given template
 * @returns Journey
 */
export function normalizeJourneyElements(journey: Journey, template: RecursivePartial<Journey>): Journey {
  return {
    ...journey,
    flights: template.flights || journey.flights,
    journeyElements: template.journeyElements || journey.journeyElements
  };
}

export const {journeys, journeyElementIds, acceptedJourney, travelerIds} = getJourneyMockData();
export const {journeys: multiLegJourneys,
  journeyElementIds: multiLegJourneyElementIds,
  acceptedJourney: acceptedMultiLegJourney,
  travelerIds: multiLegTravelerIds} = getJourneyMockData(true, false);

export const {journeys: multiLegMultiPaxJourneys,
  journeyElementIds: multiLegMultiPaxJourneyElementIds,
  acceptedJourney: acceptedMultiLegMultiPaxJourney,
  travelerIds: multiLegMultiPaxTravelerIds} = getJourneyMockData(true, true);

const roundTripJourneyTemplates = [
  multiPaxOutboundJourneyTemplate,
  multiPaxInboundJourneyTemplate,
  multiPaxRoundTripJourneyTemplate
];
const checkInStatus: CheckInStatusEnum = 'accepted';

export const roundTripJourneys = combineJourneys(roundTripJourneyTemplates).items.map((journey, index) => {
  return normalizeJourneyElements(journey, roundTripJourneyTemplates[index]);
});

export const acceptedRoundTripJourneys = [
  {
    ...roundTripJourneys[0],
    acceptance: AcceptanceRepository.ACCEPTED,
    journeyElements: roundTripJourneys[0].journeyElements.map((je) => ({
      ...je,
      journeyElement: {
        ...je.journeyElement,
        checkInStatus
      }
    }))
  },
  {
    ...roundTripJourneys[1],
    acceptance: AcceptanceRepository.ACCEPTED,
    journeyElements: roundTripJourneys[1].journeyElements.map((je) => ({
      ...je,
      journeyElement: {
        ...je.journeyElement,
        checkInStatus
      }
    }))
  },
  {
    ...roundTripJourneys[2],
    acceptance: AcceptanceRepository.ACCEPTED,
    journeyElements: roundTripJourneys[2].journeyElements.map((je) => ({
      ...je,
      journeyElement: {
        ...je.journeyElement,
        checkInStatus
      }
    }))
  }
];

export const journeyMultiNotificationFor3Adt: RecursivePartial<Journey> = {
  travelers: combineJourneyTravelers([JourneyTravelerRepository.MAN, JourneyTravelerRepository.WOMAN, JourneyTravelerRepository.MAN], {}).items,
  contacts: multipleNotificationContacts
};

export const journeyTemplateManageCheckin: RecursivePartial<Journey> = {
  travelers: combineJourneyTravelers([JourneyTravelerRepository.MAN, JourneyTravelerRepository.WOMAN], {}).items
};

export const journeysManageCheckin = combineJourneys([{
  ...journeyTemplateManageCheckin
}]).items;

export const journeyElementIdsTwoPax = journeysManageCheckin[0].journeyElements.map((je: JourneyElementCommon) => je.id);

export const journeyTwoPax = createJourney({
  template: {
    type: 'standalone',
    travelers: combineJourneyTravelers([
      JourneyTravelerRepository.MAN,
      JourneyTravelerRepository.WOMAN]).items,
    flights: combineCheckInFlightIdObjects([
      CheckInFlightIdObjectRepository.FRA_NCE]).items,
    journeyElements: combineJourneyElementCommons([
      {
        journeyElement: {
          traveler: {
            id: 'JOURNEY-TRAVELER-0-MOCK'
          }
        }
      },
      {
        journeyElement: {
          traveler: {
            id: 'JOURNEY-TRAVELER-1-MOCK'
          }
        }
      }
    ]).items,
    acceptance: {
      isAccepted: false,
      isPartial: false
    }
  }
});

export const notAcceptedjourney = createJourney({
  template: {
    ...journeyTwoPax,
    journeyElements: combineJourneyElementCommons([
      {
        journeyElement: {
          traveler: {
            id: 'JOURNEY-TRAVELER-0-MOCK'
          },
          acceptanceEligibility: {
            status: 'eligible',
            reasons: []
          }
        }
      },
      {
        journeyElement: {
          traveler: {
            id: 'JOURNEY-TRAVELER-1-MOCK'
          },
          checkInStatus: 'notAccepted',
          seat: {
            seatNumber: '1A',
            cabin: 'Y',
            seatAvailabilityStatus: 'available'
          }
        }
      }]).items,
    acceptance: createAcceptance({
      template: {
        notCheckedInJourneyElements: [
          {
            id: 'JOURNEY-ELEMENT-0-MOCK',
            journeyElement: createJourneyElement()
          },
          {
            id: 'JOURNEY-ELEMENT-1-MOCK',
            journeyElement: createJourneyElement()
          }
        ],
        isAccepted: false,
        isPartial: false
      }
    })
  }
});

export const partialAcceptedjourneyManageCheckin = createJourney({
  template: {
    ...journeyTwoPax,
    journeyElements: combineJourneyElementCommons([
      {
        journeyElement: {
          traveler: {
            id: 'JOURNEY-TRAVELER-0-MOCK'
          },
          acceptanceEligibility: {
            status: 'eligible',
            reasons: []
          }
        }
      },
      {
        journeyElement: {
          traveler: {
            id: 'JOURNEY-TRAVELER-1-MOCK'
          },
          checkInStatus: 'accepted',
          seat: {
            seatNumber: '1A',
            cabin: 'Y',
            seatAvailabilityStatus: 'available'
          }
        }
      }]).items,
    acceptance: createAcceptance({
      template: {
        checkedInJourneyElements: [
          {
            id: 'JOURNEY-ELEMENT-1-MOCK',
            journeyElement: createJourneyElement()
          }
        ],
        notCheckedInJourneyElements: [
          {
            id: 'JOURNEY-ELEMENT-0-MOCK',
            journeyElement: createJourneyElement()
          }
        ],
        isAccepted: false,
        isPartial: true
      }
    })
  }
});

export const acceptedJourneyFirstPax = createJourney({
  template: {
    ...journeyTwoPax,
    travelers: combineJourneyTravelers([
      JourneyTravelerRepository.WOMAN]).items,
    journeyElements: combineJourneyElementCommons([
      {
        journeyElement: {
          traveler: {
            id: 'JOURNEY-TRAVELER-0-MOCK'
          },
          flightId: 'CHECK-IN-FLIGHT-0-MOCK',
          checkInStatus: 'accepted',
          seat: {
            seatNumber: '4F',
            cabin: 'Y',
            seatAvailabilityStatus: 'available'
          }
        }
      }]).items,
    acceptance: {
      checkedInJourneyElements: [{
        id: 'JOURNEY-ELEMENT-0-MOCK',
        journeyElement: createJourneyElement()
      }],
      isAccepted: true,
      isPartial: false
    }
  }
});

export const acceptedJourneySecondPax = createJourney({
  template: {
    ...journeyTwoPax,
    travelers: combineJourneyTravelers([
      JourneyTravelerRepository.MAN]).items,
    journeyElements: combineJourneyElementCommons([
      {
        journeyElement: {
          traveler: {
            id: 'JOURNEY-TRAVELER-1-MOCK'
          },
          flightId: 'CHECK-IN-FLIGHT-0-MOCK',
          checkInStatus: 'accepted',
          seat: {
            seatNumber: '5F',
            cabin: 'Y',
            seatAvailabilityStatus: 'available'
          }
        }
      }]).items,
    acceptance: {
      checkedInJourneyElements: [{
        id: 'JOURNEY-ELEMENT-0-MOCK',
        journeyElement: createJourneyElement()
      }],
      isAccepted: true,
      isPartial: false
    }
  }
});

export const fullyAcceptedjourney = createJourney({
  template: {
    ...journeyTwoPax,
    journeyElements: combineJourneyElementCommons([
      {
        journeyElement: {
          traveler: {
            id: 'JOURNEY-TRAVELER-0-MOCK'
          },
          checkInStatus: 'accepted',
          seat: {
            seatNumber: '1A',
            cabin: 'Y',
            seatAvailabilityStatus: 'available'
          }
        }
      },
      {
        journeyElement: {
          traveler: {
            id: 'JOURNEY-TRAVELER-1-MOCK'
          },
          checkInStatus: 'accepted',
          seat: {
            seatNumber: '5F',
            cabin: 'Y',
            seatAvailabilityStatus: 'available'
          }
        }
      }]).items,
    acceptance: createAcceptance({
      template: {
        checkedInJourneyElements: [
          {
            id: 'JOURNEY-ELEMENT-1-MOCK',
            journeyElement: createJourneyElement()
          }
        ],
        notCheckedInJourneyElements: [
          {
            id: 'JOURNEY-ELEMENT-0-MOCK',
            journeyElement: createJourneyElement()
          }
        ],
        isAccepted: true,
        isPartial: false
      }
    })
  }
});

export const journeyMultiNotificationFor1Adt1Chd1Inf: RecursivePartial<Journey> = {
  travelers: combineJourneyTravelers([JourneyTravelerRepository.MAN, JourneyTravelerRepository.CHD, JourneyTravelerRepository.INF], {}).items,
  contacts: multipleNotificationContacts
};

const mockFlight1 = createCheckInFlight({
  template:
  {
    ...CheckInFlightIdObjectRepository.NCE_LHR,
    acceptanceStatus: 'opened'
  }
});

const mockFlight2 = createCheckInFlight({
  template:
  {
    ...CheckInFlightIdObjectRepository.LHR_FRA,
    acceptanceStatus: 'opened'
  }
});

const traveler1 = createTraveler({
  template: {
    passengerTypeCode: 'ADT',
    names: [{
      firstName: 'John',
      lastName: 'Doe',
      title: 'MR'
    }],
    id: 'JOURNEY-TRAVELER-0-MOCK'
  }
});

const traveler2 = createTraveler({
  template: {
    passengerTypeCode: 'ADT',
    names: [{
      firstName: 'Sofia',
      lastName: 'Doe',
      title: 'MR'
    }],
    id: 'JOURNEY-TRAVELER-1-MOCK'
  }
});

export const journey2Adt2Seg: MockJourney[] = combineJourneys([
  {
    travelers: [traveler1, traveler2],
    flights: combineCheckInFlightIdObjects([
      mockFlight1,
      mockFlight2
    ]).items,
    acceptanceEligibility: createEligibility({
      template: {
        status: 'eligible'
      }
    }),
    journeyElements: [
      {
        id: 'JOURNEY-ELEMENT-0-MOCK',
        journeyElement: createJourneyElement({
          template: {
            acceptanceEligibility: createEligibility({
              template: {
                status: 'eligible'
              }
            }),
            boardingPassEligibility: createBoardingPassEligibility({
              template: {
                status: 'eligible'
              }
            }),
            travelerId: 'JOURNEY-TRAVELER-0-MOCK',
            flightId: 'CHECK-IN-FLIGHT-0-MOCK',
            flight: mockFlight1
          }
        })
      },
      {
        id: 'JOURNEY-ELEMENT-1-MOCK',
        journeyElement: createJourneyElement({
          template: {
            acceptanceEligibility: createEligibility({
              template: {
                status: 'ineligible',
                reasons: ['flightAcceptanceStatus']
              }
            }),
            boardingPassEligibility: createBoardingPassEligibility({
              template: {
                status: 'eligible'
              }
            }),
            travelerId: 'JOURNEY-TRAVELER-0-MOCK',
            flightId: 'CHECK-IN-FLIGHT-1-MOCK',
            flight: mockFlight2
          }
        })
      },
      {
        id: 'JOURNEY-ELEMENT-2-MOCK',
        journeyElement: createJourneyElement({
          template: {
            acceptanceEligibility: createEligibility({
              template: {
                status: 'eligible'
              }
            }),
            boardingPassEligibility: createBoardingPassEligibility({
              template: {
                status: 'eligible'
              }
            }),
            travelerId: 'JOURNEY-TRAVELER-1-MOCK',
            flightId: 'CHECK-IN-FLIGHT-0-MOCK',
            flight: mockFlight1
          }
        })
      },
      {
        id: 'JOURNEY-ELEMENT-3-MOCK',
        journeyElement: createJourneyElement({
          template: {
            acceptanceEligibility: createEligibility({
              template: {
                status: 'ineligible',
                reasons: ['flightAcceptanceStatus']
              }
            }),
            boardingPassEligibility: createBoardingPassEligibility({
              template: {
                status: 'eligible'
              }
            }),
            travelerId: 'JOURNEY-TRAVELER-1-MOCK',
            flightId: 'CHECK-IN-FLIGHT-1-MOCK',
            flight: mockFlight2
          }
        })
      }
    ]
  }
]).items;

export const acceptedjourney2Adt2Seg: MockJourney = createJourney({
  template: {
    ...journey2Adt2Seg[0],
    acceptance: AcceptanceRepository.ACCEPTED
  }
});

export const ineligibleJourney = createJourney({
  template: {
    type: 'standalone',
    travelers: combineJourneyTravelers([
      JourneyTravelerRepository.MAN]).items,
    flights: combineCheckInFlightIdObjects([
      CheckInFlightIdObjectRepository.NCE_FRA]).items,
    id: 'JOURNEY-1-MOCK',
    journeyElements: combineJourneyElementCommons([
      {
        journeyElement: {
          traveler: {
            id: 'JOURNEY-TRAVELER-4-MOCK'
          }
        }
      }]).items,
    acceptance: {
      isAccepted: false,
      isPartial: false
    },
    acceptanceEligibility:
    {
      status: 'ineligible',
      reasons: ['OUTBOUND_NOT_CHECKEDIN']
    }
  }
});
